import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Crown, CheckCircle, X, Sparkles, Zap, Building2, Shield,
  Loader, CreditCard, Calendar, ArrowRight, Check, Star,
  AlertCircle, RefreshCw
} from 'lucide-react';

// Plan Card Component
const PlanCard = ({ plan, planId, currentPlan, onSelect, billingCycle }) => {
  const isCurrentPlan = currentPlan === planId;
  const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
  const monthlyPrice = billingCycle === 'yearly' ? (plan.price_yearly / 12).toFixed(0) : plan.price_monthly;
  
  const planColors = {
    free: 'border-slate-200 bg-white',
    pro: 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500',
    enterprise: 'border-purple-500 bg-purple-50'
  };
  
  const buttonColors = {
    free: 'bg-slate-600 hover:bg-slate-700',
    pro: 'bg-emerald-600 hover:bg-emerald-700',
    enterprise: 'bg-purple-600 hover:bg-purple-700'
  };

  return (
    <div 
      className={`relative rounded-2xl border-2 p-6 transition-all ${planColors[planId]} ${
        planId === 'pro' ? 'scale-105 shadow-xl' : 'hover:shadow-lg'
      }`}
      data-testid={`plan-${planId}`}
    >
      {planId === 'pro' && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-emerald-600 text-white text-sm font-bold rounded-full flex items-center gap-1">
          <Star size={14} /> MOST POPULAR
        </div>
      )}
      
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-slate-900">{plan.name}</h3>
        <div className="mt-4">
          <span className="text-4xl font-bold text-slate-900">${monthlyPrice}</span>
          <span className="text-slate-500">/month</span>
        </div>
        {billingCycle === 'yearly' && price > 0 && (
          <p className="text-sm text-emerald-600 mt-1">
            ${price}/year (Save 2 months)
          </p>
        )}
      </div>
      
      <ul className="space-y-3 mb-6">
        {plan.features?.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
            <Check size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
            {feature}
          </li>
        ))}
      </ul>
      
      <button
        onClick={() => onSelect(planId)}
        disabled={isCurrentPlan}
        className={`w-full py-3 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
          isCurrentPlan ? 'bg-slate-400 cursor-not-allowed' : buttonColors[planId]
        }`}
        data-testid={`select-plan-${planId}`}
      >
        {isCurrentPlan ? (
          <>Current Plan</>
        ) : planId === 'free' ? (
          <>Downgrade to Free</>
        ) : (
          <>
            <Sparkles size={18} />
            {planId === 'enterprise' ? 'Contact Sales' : 'Upgrade Now'}
          </>
        )}
      </button>
    </div>
  );
};

// Current Subscription Card
const CurrentSubscriptionCard = ({ subscription }) => {
  if (!subscription) return null;
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8" data-testid="current-subscription">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${
            subscription.is_premium ? 'bg-emerald-100' : 'bg-slate-100'
          }`}>
            <Crown size={24} className={subscription.is_premium ? 'text-emerald-600' : 'text-slate-400'} />
          </div>
          <div>
            <h3 className="font-bold text-slate-900">
              {subscription.plan?.toUpperCase()} Plan
            </h3>
            <p className="text-sm text-slate-500">
              {subscription.is_premium ? 'Premium features enabled' : 'Basic features only'}
            </p>
          </div>
        </div>
        
        {subscription.expires_at && (
          <div className="text-right">
            <p className="text-sm text-slate-500">Renews on</p>
            <p className="font-medium text-slate-900">
              {new Date(subscription.expires_at).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
      
      {subscription.is_premium && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-sm text-slate-600">Premium Features Active:</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {subscription.limits?.premium_features?.map((feature, idx) => (
              <span key={idx} className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                {feature.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Payment Success Modal
const PaymentSuccessModal = ({ onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center">
      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <CheckCircle size={32} className="text-emerald-600" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h2>
      <p className="text-slate-600 mb-6">
        Your subscription has been activated. You now have access to all premium features.
      </p>
      <button
        onClick={onClose}
        className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
        data-testid="close-success-modal"
      >
        Start Exploring
      </button>
    </div>
  </div>
);

// Main Subscription Page
const Subscription = () => {
  const [plans, setPlans] = useState({});
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showSuccess, setShowSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    const [plansData, subData] = await Promise.all([
      apiFetch(`${API}/subscription/plans`),
      apiFetch(`${API}/subscription/current`)
    ]);

    if (plansData?._error || subData?._error) {
      console.error('Failed to fetch:', plansData?.message || subData?.message);
      setLoading(false);
      return;
    }

    setPlans(plansData.plans || {});
    setCurrentSubscription(subData);
    setLoading(false);
  }, []);

  // Poll payment status
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      return;
    }

    const data = await apiFetch(`${API}/subscription/checkout-status/${sessionId}`);

    if (data?._error) {
      console.error('Error checking payment:', data.message);
      setCheckingPayment(false);
      return;
    }

    if (data.payment_status === 'paid') {
      setShowSuccess(true);
      setCheckingPayment(false);
      fetchData(); // Refresh subscription data
      // Clear session_id from URL
      navigate('/subscription', { replace: true });
      return;
    }

    if (data.status === 'expired') {
      setCheckingPayment(false);
      return;
    }

    // Continue polling
    setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
  }, [fetchData, navigate]);

  useEffect(() => {
    fetchData();
    
    // Check if returning from Stripe
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      setCheckingPayment(true);
      pollPaymentStatus(sessionId);
    }
  }, [fetchData, searchParams, pollPaymentStatus]);

  const handleSelectPlan = async (planId) => {
    if (planId === 'free') {
      // Handle downgrade
      return;
    }
    
    if (planId === 'enterprise') {
      // Contact sales
      window.open('mailto:sales@example.com?subject=Enterprise Plan Inquiry', '_blank');
      return;
    }
    
    const data = await apiFetch(`${API}/subscription/create-checkout`, {
      method: 'POST',
      body: {
        plan_id: planId,
        billing_cycle: billingCycle,
        origin_url: window.location.origin
      }
    });

    if (data?._error) {
      console.error('Failed to create checkout:', data.message);
      return;
    }

    // Redirect to Stripe checkout
    window.location.href = data.checkout_url;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="animate-spin text-emerald-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="subscription-page">
      {showSuccess && <PaymentSuccessModal onClose={() => setShowSuccess(false)} />}
      
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-8 text-white">
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold mb-2">Subscription</h1>
          <p className="text-emerald-100">
            Unlock premium regulatory compliance features and advanced AI capabilities
          </p>
        </div>
      </div>

      {/* Current Subscription */}
      <CurrentSubscriptionCard subscription={currentSubscription} />

      {/* Payment Processing Indicator */}
      {checkingPayment && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
          <Loader className="animate-spin text-blue-600" size={20} />
          <p className="text-blue-700">Verifying your payment...</p>
        </div>
      )}

      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4">
        <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-slate-900' : 'text-slate-400'}`}>
          Monthly
        </span>
        <button
          onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
          className={`relative w-14 h-7 rounded-full transition-colors ${
            billingCycle === 'yearly' ? 'bg-emerald-600' : 'bg-slate-300'
          }`}
          data-testid="billing-toggle"
        >
          <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
            billingCycle === 'yearly' ? 'translate-x-8' : 'translate-x-1'
          }`} />
        </button>
        <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-slate-900' : 'text-slate-400'}`}>
          Yearly
          <span className="ml-1 text-emerald-600">(Save 17%)</span>
        </span>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        {Object.entries(plans).map(([planId, plan]) => (
          <PlanCard
            key={planId}
            plan={plan}
            planId={planId}
            currentPlan={currentSubscription?.plan}
            onSelect={handleSelectPlan}
            billingCycle={billingCycle}
          />
        ))}
      </div>

      {/* Feature Comparison */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mt-8">
        <h3 className="font-bold text-slate-900 mb-4">Premium Features Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 font-medium text-slate-600">Feature</th>
                <th className="text-center py-3 font-medium text-slate-600">Free</th>
                <th className="text-center py-3 font-medium text-emerald-600">Pro</th>
                <th className="text-center py-3 font-medium text-purple-600">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              {[
                { name: 'IRO Assessment Matrix', free: false, pro: true, enterprise: true },
                { name: 'SEC Climate Notes', free: false, pro: true, enterprise: true },
                { name: 'GRI Sector Standards', free: false, pro: true, enterprise: true },
                { name: 'Automated Workflows', free: false, pro: true, enterprise: true },
                { name: 'Unlimited Data Uploads', free: false, pro: true, enterprise: true },
                { name: 'AI Queries/month', free: '20', pro: '500', enterprise: 'Unlimited' },
                { name: 'Custom AI Training', free: false, pro: false, enterprise: true },
                { name: 'API Access', free: false, pro: false, enterprise: true },
                { name: 'Dedicated Support', free: false, pro: false, enterprise: true },
              ].map((row, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="py-3 text-slate-700">{row.name}</td>
                  <td className="py-3 text-center">
                    {typeof row.free === 'boolean' ? (
                      row.free ? <Check className="mx-auto text-emerald-500" size={18} /> : <X className="mx-auto text-slate-300" size={18} />
                    ) : (
                      <span className="text-slate-600">{row.free}</span>
                    )}
                  </td>
                  <td className="py-3 text-center">
                    {typeof row.pro === 'boolean' ? (
                      row.pro ? <Check className="mx-auto text-emerald-500" size={18} /> : <X className="mx-auto text-slate-300" size={18} />
                    ) : (
                      <span className="text-emerald-600 font-medium">{row.pro}</span>
                    )}
                  </td>
                  <td className="py-3 text-center">
                    {typeof row.enterprise === 'boolean' ? (
                      row.enterprise ? <Check className="mx-auto text-emerald-500" size={18} /> : <X className="mx-auto text-slate-300" size={18} />
                    ) : (
                      <span className="text-purple-600 font-medium">{row.enterprise}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
